import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import {
    getClaimsInfoByIdQueryOptions,
    getCompanyAssignedSubclaimsOfClaimQueryOptions
} from "@/api/queryOptions/claimsApiQueryOptions";
import { InvoiceFormData } from "@/types";
import { pickSubClaim } from "@/utils/subClaimUtils";

type EditorState = {
    claimRef: string | null;
    subClaimRef: string | null;
    clientName: string;
    vesselName: string;
};

const EMPTY_DRAFT: EditorState = { claimRef: null, subClaimRef: null, clientName: "", vesselName: "" };

export const useClaimEditor = (crmId?: string) => {
    const queryClient = useQueryClient();
    const { watch, getValues, setValue } = useFormContext<InvoiceFormData>();
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(EMPTY_DRAFT);

    const startEditing = useCallback(() => {
        const { claimRef, subClaimsRef, clientName, vesselName } = getValues();
        setDraft({
            claimRef: claimRef || null,
            subClaimRef: subClaimsRef ?? null,
            clientName: clientName ?? "",
            vesselName: vesselName ?? ""
        });
        setIsEditing(true);
    }, [getValues]);

    const cancelEditing = useCallback(() => {
        setIsEditing(false);
        setDraft(EMPTY_DRAFT);
    }, []);

    const saveEditing = useCallback(() => {
        if (!draft.subClaimRef) {
            return;
        }

        if (draft.claimRef) {
            setValue("claimRef", draft.claimRef);
            setValue("clientName", draft.clientName);
            setValue("vesselName", draft.vesselName);
        }

        setValue("subClaimsRef", draft.subClaimRef);
        setIsEditing(false);
    }, [draft, setValue]);

    const selectSubClaim = useCallback(
        (subClaimRef: string | null) => {
            if (isEditing) {
                setDraft(prev => ({ ...prev, subClaimRef }));
            } else {
                setValue("subClaimsRef", subClaimRef ?? undefined);
            }
        },
        [isEditing, setValue]
    );

    const updateClaim = useCallback(
        async (claimRef: string, clientName: string, vesselName: string) => {
            if (isEditing) {
                setDraft({ claimRef, clientName, vesselName, subClaimRef: null });
            } else {
                setValue("claimRef", claimRef);
                setValue("clientName", clientName);
                setValue("vesselName", vesselName);
            }
            selectSubClaim(null);

            const claim = await queryClient.fetchQuery(getClaimsInfoByIdQueryOptions(claimRef)).catch(() => null);
            const assignedRefs = crmId
                ? await queryClient
                      .fetchQuery(getCompanyAssignedSubclaimsOfClaimQueryOptions(claimRef, crmId))
                      .catch(() => [])
                : [];

            const subClaimRef = pickSubClaim(claim?.subClaims, assignedRefs);
            if (subClaimRef) {
                selectSubClaim(subClaimRef);
            }
        },
        [crmId, isEditing, queryClient, selectSubClaim, setValue]
    );

    return {
        isEditing,
        draft,
        startEditing,
        cancelEditing,
        saveEditing,
        updateClaim,
        canSaveEditing: Boolean(draft.subClaimRef),
        formValues: watch(),
        updateSubClaim: selectSubClaim
    };
};
